import React, { useState, useMemo } from 'react';
import { styled } from 'styletron-react';
import { HeadingMedium, ParagraphMedium } from 'baseui/typography';
import { TableContent } from '../LayoutPage';
import { MantineDataTable } from '../../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useUserTypes } from '../../hooks/useUserTypes';
import { UserTypeDetailDrawer } from '../../components/UserTypeDetailDrawer';
import type { UserType } from '../../types/role.types';
import { Plus, Users } from 'lucide-react';
import { useUpdateUserType } from '../../hooks/useUserTypes';

const Container = styled('div', {
  padding: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const Header = styled('div', {
  marginBottom: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

const HeaderLeft = styled('div', {});

const TableContainer = styled('div', {
  flex: 1,
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: '20px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const EmptyState = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px',
  gap: '16px',
  color: '#6B7280',
});

const AddButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--tenant-primary, #111827)',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
});

export const Roles = () => {
  const { userTypes, isLoading } = useUserTypes();
  const updateUserType = useUpdateUserType();
  const [selectedUserType, setSelectedUserType] = useState<UserType | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('edit');

  const handleToggleField = (userType: UserType, field: 'is_register' | 'is_new_rol_request', e: React.MouseEvent) => {
    e.stopPropagation();
    updateUserType.mutate({
      id: userType.id,
      data: { name: userType.name, [field]: !userType[field] },
    });
  };

  const columns = useMemo<MRT_ColumnDef<UserType>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 60,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 180,
        Cell: ({ cell }) => cell.getValue<string>(),
      },
      {
        accessorKey: 'is_register',
        header: 'Available in Registration',
        size: 180,
        Cell: ({ row }) => {
          const ut = row.original;
          return (
            <div onClick={(e) => handleToggleField(ut, 'is_register', e)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: '40px', height: '22px', borderRadius: '11px',
                backgroundColor: ut.is_register ? 'var(--tenant-primary, #111827)' : '#D1D5DB',
                position: 'relative', transition: 'background-color 0.2s ease',
              }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#fff',
                  position: 'absolute', top: '2px', left: ut.is_register ? '20px' : '2px',
                  transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'is_new_rol_request',
        header: 'Requires Estate Selection',
        size: 180,
        Cell: ({ row }) => {
          const ut = row.original;
          return (
            <div onClick={(e) => handleToggleField(ut, 'is_new_rol_request', e)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: '40px', height: '22px', borderRadius: '11px',
                backgroundColor: ut.is_new_rol_request ? 'var(--tenant-primary, #111827)' : '#D1D5DB',
                position: 'relative', transition: 'background-color 0.2s ease',
              }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#fff',
                  position: 'absolute', top: '2px', left: ut.is_new_rol_request ? '20px' : '2px',
                  transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
          );
        },
      },
    ],
    []
  );

  const handleRowClick = (userType: UserType) => {
    setSelectedUserType(userType);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  };

  const handleCreate = () => {
    setSelectedUserType(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedUserType(null);
  };

  return (
    <TableContent>
      <Container>
        <Header>
          <HeaderLeft>
            <HeadingMedium marginTop="0" marginBottom="8px">
              Roles
            </HeadingMedium>
            <ParagraphMedium margin={0} color="#6B7280">
              Manage the roles available in the application
            </ParagraphMedium>
          </HeaderLeft>
          <AddButton onClick={handleCreate}>
            <Plus size={16} />
            Add Role
          </AddButton>
        </Header>

        <TableContainer>
          {userTypes.length === 0 && !isLoading ? (
            <EmptyState>
              <Users size={48} color="#D1D5DB" />
              <div style={{ textAlign: 'center' }}>
                <ParagraphMedium margin={0} style={{ fontWeight: 500 }}>
                  No roles found
                </ParagraphMedium>
                <ParagraphMedium margin="8px 0 0 0" style={{ fontSize: '14px' }}>
                  Click "Add Role" to create a new role
                </ParagraphMedium>
              </div>
            </EmptyState>
          ) : (
            <MantineDataTable
              columns={columns}
              data={userTypes}
              loading={isLoading}
              enablePagination
              enableSorting
              enableGlobalFilter={false}
              initialPageSize={20}
              pageSizeOptions={[10, 20, 50]}
              onRowClick={handleRowClick}
            />
          )}
        </TableContainer>

        <UserTypeDetailDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          userType={selectedUserType}
          mode={drawerMode}
        />
      </Container>
    </TableContent>
  );
};
